import { Alert, Button, Select, Slider, Tooltip, Typography } from "antd"
import TimePicker from "../../../common/TimePicker"
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  MeasuringStrategy
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ReorderDayViewState, ReorderEventLocation, formatTime, formatDistance } from "./reorderDayViewModel"
import SortableEvent from "./SortableEvent"
import dayjs from "dayjs"

const { Text } = Typography

type ReorderDayViewEventListProps = {
  state: ReorderDayViewState
  totalTime: number
  distance: number
  locations: ReorderEventLocation[]
  hoveredEventId: number | null
  setHoveredEventId: (id: number | null) => void
  onStartTimeChange: (time: string) => void
  onStartingLocationChange: (value: string) => void
  onEndingLocationChange: (value: string) => void
  onDragEnd: (event: DragEndEvent) => void
  onOptimizeDay: () => void
  onEventLocationUpdate: (eventId: number, latitude: number, longitude: number) => void
  optimizeRange: [number, number]
  onOptimizeRangeChange: (range: [number, number]) => void
  showFilterWarning: boolean
}

const ReorderDayViewEventList = ({
  state,
  totalTime,
  distance,
  locations,
  hoveredEventId,
  setHoveredEventId,
  onStartTimeChange,
  onStartingLocationChange,
  onEndingLocationChange,
  onDragEnd,
  onOptimizeDay,
  onEventLocationUpdate,
  optimizeRange,
  onOptimizeRangeChange,
  showFilterWarning
}: ReorderDayViewEventListProps) => {
  const selectedCount = optimizeRange[1] - optimizeRange[0] + 1
  const exceedsLimit = selectedCount > 23

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  return (
    <div style={{
      width: 400,
      background: '#fafbfc',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'scroll',
      height: '66vh'
    }}>
      {showFilterWarning &&
        <Alert type="warning" message="Events that are scheduled as all-day or that span multiple days are not included in day optimization" style={{ margin: 16 }} />
      }
      <div style={{ padding: '16px 15px 0' }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e0e0e0' }}>
          {dayjs(state.startTime).format('dddd, MMMM D, YYYY')}
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 500, marginBottom: 4, fontSize: 14 }}>Start Time</div>
          <TimePicker
            value={dayjs(state.startTime)}
            onChange={(time) => {
              const newDateTime = dayjs(state.startTime)
                .hour(time.hour())
                .minute(time.minute())
                .second(0)
              onStartTimeChange(newDateTime.toISOString())
            }}
          />
        </div>

        {state.events.length <= 23 ? (
          <Tooltip title="Optimize events for shortest drive time">
            <Button
              type="default"
              onClick={onOptimizeDay}
              style={{ width: '100%', marginBottom: 12 }}
            >
              Optimize Day
            </Button>
          </Tooltip>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <Tooltip title={exceedsLimit ? "Select 23 or fewer events" : "Optimize selected events for shortest drive time"}>
              <span>
                <Button
                  type="default"
                  onClick={onOptimizeDay}
                  style={{ width: '100%' }}
                  disabled={exceedsLimit}
                >
                  Optimize Day
                </Button>
              </span>
            </Tooltip>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Select events to optimize (max 23)
              </Text>
              <Slider
                range
                min={1}
                max={state.events.length}
                value={optimizeRange}
                onChange={(value) => onOptimizeRangeChange(value as [number, number])}
                tooltip={{ formatter: (value) => value !== undefined ? `Event ${value}` : '' }}
                style={{ marginTop: 4 }}
              />
              <Text type={exceedsLimit ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                Event {optimizeRange[0]} - Event {optimizeRange[1]} ({selectedCount} events)
                {exceedsLimit && ' - exceeds limit'}
              </Text>
            </div>
          </div>
        )}

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 12,
          fontSize: 14,
          color: '#666',
          paddingTop: 4,
          borderTop: '1px solid #e0e0e0',
          paddingBottom: 4,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <span style={{ fontWeight: 500 }}>Drive Time: {formatTime(totalTime)}</span>
          <span style={{ fontWeight: 500 }}>Distance: {formatDistance(distance)} mi</span>
        </div>

        {state.startingLocation && (
          <div style={{
            padding: 8,
            background: '#f5f5f5',
            borderRadius: 8,
            marginBottom: 12
          }}>
            <div style={{ fontWeight: 500, marginBottom: 4, fontSize: 14 }}>Starting Location</div>
            <Select
              style={{ width: '100%', height: 65, marginBottom: 2 }}
              value={state.startingLocation?.id}
              onChange={onStartingLocationChange}
              options={locations.map(location => ({
                value: location.id,
                label: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 500, color: '#000' }}>{location.jobName}</span>
                    <span style={{ fontSize: 12, color: '#666' }}>
                      {location.address1}{location.address2 ? `, ${location.address2}` : ''}, {location.city}, {location.stateCode} {location.zip}
                    </span>
                  </div>
                )
              }))}
              placeholder="Select starting location"
              listHeight={200}
              dropdownStyle={{ overflowY: 'scroll' }}
            />
          </div>
        )}
      </div>

      {state.events && state.events.length > 0 ? (
        <DndContext
          onDragEnd={onDragEnd}
          sensors={sensors}
          collisionDetection={closestCenter}
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.Always,
            },
          }}
        >
          <SortableContext
            items={state.events.map(event => event.id)}
            strategy={verticalListSortingStrategy}
          >
            <div style={{ padding: '0 15px' }}>
              {state.events.map((event, index) => {
                const isStartingLocation = state.startingLocation && state.startingLocation.id === `event-${event.id}`
                const isEndingLocation = state.endingLocation && state.endingLocation.id === `event-${event.id}`

                if (isStartingLocation || isEndingLocation) {
                  return null
                }

                return (
                  <SortableEvent
                    key={event.id}
                    event={event}
                    index={index}
                    isHovered={hoveredEventId === event.id}
                    onMouseEnter={() => setHoveredEventId(event.id)}
                    onMouseLeave={() => setHoveredEventId(null)}
                    disabled={false}
                    onLocationUpdate={onEventLocationUpdate}
                  />
                )
              })}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div style={{
          border: '1.5px dashed #d3dae6',
          borderRadius: 12,
          minHeight: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#7a869a',
          fontSize: 16,
          margin: '0 15px 16px 15px',
        }}>
          No Events
        </div>
      )}

      <div style={{ padding: '0 15px 16px' }}>
        {state.endingLocation && (
          <div style={{
            padding: 8,
            background: '#f5f5f5',
            borderRadius: 8,
            marginBottom: 2
          }}>
            <div style={{ fontWeight: 500, marginBottom: 4, fontSize: 14 }}>Ending Location</div>
            <Select
              style={{ width: '100%', marginBottom: 2, height: 65 }}
              value={state.endingLocation?.id}
              onChange={onEndingLocationChange}
              options={locations.map(location => ({
                value: location.id,
                label: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 500, color: '#000' }}>{location.jobName}</span>
                    <span style={{ fontSize: 12, color: '#666' }}>
                      {location.address1}{location.address2 ? `, ${location.address2}` : ''}, {location.city}, {location.stateCode} {location.zip}
                    </span>
                  </div>
                )
              }))}
              placeholder="Select ending location"
              listHeight={200}
              dropdownStyle={{ overflowY: 'scroll' }}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default ReorderDayViewEventList
